import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Modal,
  Pagination,
  Row,
  Table,
  Tooltip,
  Space,
  message,
} from "antd";
import { jwtDecode } from "jwt-decode";
import find from "lodash/find";
import isEmpty from "lodash/isEmpty";
import pick from "lodash/pick";
import uniq from "lodash/uniq";
import moment from "moment";
import axios from "axios";
import { useHistory } from "react-router";
import { useRouteMatch } from "react-router-dom";
import { ReactComponent as DotsIcon } from "../../img/3dots.svg";
import warningIcon from "../../img/warning";
import { MENU_ACCOUNT_CHARGES_WRITE } from "../../lib/const/permissions";
import { adjustmentIcon, chargeIcon } from "../../lib/icons";
import { buildQueryStringWithObjectParams, formatDate, round } from "../../lib/utils";
import { checkIsLoaded, checkIsLoading } from "../../lib/utils/checkLoadingStatus";
import {
  displayFormat,
  toDisplayFormat,
  toHttpFormatEndOfDay,
  toHttpFormatStartOfDay,
} from "../../lib/utils/formatDate";
import hideButtonVisitMark from "../../lib/utils/hideButtonVisitMark";
import AuthService from "../../services/AuthService";
import { createPostBill } from "../../store/slices/bills";
import {
  createCharge,
  createUpdateAdjustment,
  fetchChargesByAggId,
  fetchServiceByAggIdForAdjustmentCreate,
  clearServiceForAdjustmentCreate,
} from "../../store/slices/charges";
import { fetchAgrStatus } from "../../store/slices/rtkDictionaries";
import { createInvoices } from "../../store/slices/invoices";
import { exportToXLS } from "../../store/slices/xlsExport";
import HistoryAndPropertyModal from "../HistoryAndPropertyModal/HistoryAndPropertyModal";
import { hasAccess } from "../RolesModule/hasAccess";
import { limitText } from "../../lib/utils/limitText";
import ToggleColumns from "../ToggleableVersionsTable/ToggleColumns";
import SpanNumberFormat from "../common/SpanNumberFormat";
import Message from "../Message/Message";
import { getRoute } from "../../lib/routes";
import { AccountChargesSearchFilters } from "../AccountChargesSearchFilters/AccountChargesSearchFilters";
import { useAppDispatch, useAppSelector } from "../..";
import { CreateChargeModal } from "./CreateChargeModal/CreateChargeModal";
import { CreateAdjustmentModal } from "./CreateAdjustmentModal/CreateAdjustmentModal";
import { useLoading } from "../../lib/hooks/useLoading";
import "./AccountCharges.less";

const AccountCharges = (props) => {
  const {
    data,
    checked,
    chargesData,
    accountId,
    getValueChargesPagingAndFilters,
    resetSearchCharges,
    handleSearchCharges,
    toggleZeroCharges,
    chargesSearchParams,
    chargeParams,
    handlePageSize,
    formFilters,
    fetchChargesAll,
    selectedRowKeys,
    setSelectedRowKeys,
  } = props;

  const localStore = JSON.parse(sessionStorage.getItem("chargeFilters"));

  const { rateType, rateTypeLoading, serviceStatus, agrStatusLoading, agrStatus } = useAppSelector(
    (state) => state.rtkDictionaries
  );
  const { createUpdateAdjustmentLoading, chargesLoading } = useAppSelector(
    (state) => state.charges
  );
  const { agreements } = useAppSelector((state) => state.agreements);

  const { serviceForAdjustmentCreate, serviceForAdjustmentCreateLoading } = useAppSelector(
    (state) => state.charges
  );
  const isServicesLoading = useMemo(() => checkIsLoading(serviceForAdjustmentCreateLoading), [
    serviceForAdjustmentCreateLoading,
  ]);
  const [filtersCollapseState, setFiltersCollapseState] = useState("unfolded");
  const [visibleCreateAdjustmentModal, setVisibleCreateAdjustmentModal] = useState(false);
  const [visibleCreateChargeModal, setVisibleCreateChargeModal] = useState(false);
  const [visibleConfirmCreateAdjustmentModal, setVisibleConfirmCreateAdjustmentModal] = useState(
    false
  );
  const [createInvoiceModalData, setCreateInvoiceModalData] = useState({ visible: false });
  const [createBillModalData, setCreateBillModalData] = useState({ visible: false });
  const [chargeColumns, setChargeColumns] = useState("");
  const [object, setObject] = useState("");
  const defaultSelectedStatuses = useMemo(
    () => agrStatus.map(({ id }) => id).filter((id) => id !== 6),
    [agrStatus]
  );
  const [loadServicesData, setLoadServicesData] = useState();
  const history = useHistory();
  const { url } = useRouteMatch();
  const [formCreateAdjustment] = Form.useForm();
  const dispatch = useAppDispatch();

  const { isLoading, startLoading, stopLoading } = useLoading();

  // Загрузка услуг по договору, после выбора
  useEffect(() => {
    if (loadServicesData?.agreementId) {
      dispatch(
        fetchServiceByAggIdForAdjustmentCreate({
          agreementsIds: [loadServicesData.agreementId],
          includePastDates: loadServicesData?.includePastDates,
        })
      );
    }
  }, [loadServicesData]);

  // Очистка списка услуг и данных для их загрузки по закрытии модалки создания начисления
  useEffect(() => {
    if (!visibleCreateChargeModal) {
      setLoadServicesData(undefined);
      dispatch(clearServiceForAdjustmentCreate());
    }
  }, [visibleCreateChargeModal]);

  // Очистка списка услуг и данных для их загрузки по закрытии модалки создания корректировки
  useEffect(() => {
    if (!visibleCreateAdjustmentModal) {
      setLoadServicesData(undefined);
      dispatch(clearServiceForAdjustmentCreate());
    }
  }, [visibleCreateAdjustmentModal]);

  // Загрузка справочника статусов договора
  useEffect(() => {
    if (visibleCreateChargeModal || visibleCreateAdjustmentModal) {
      if (!checkIsLoaded(agrStatusLoading)) {
        dispatch(fetchAgrStatus());
      }
    }
  }, [visibleCreateAdjustmentModal, visibleCreateChargeModal]);

  useEffect(() => {
    setChargeColumns(localStore?.chargeColumns);
  }, []);

  const getTableHeight = () => (filtersCollapseState === "unfolded" ? 450 : 560);

  const resetSearchPaymentsAndResetSetSelectedRowKeys = () => {
    resetSearchCharges();
    setSelectedRowKeys([]);
  };

  const handleSearchChargesAndResetSelectedRowKeys = () => {
    handleSearchCharges(chargeParams);
    setSelectedRowKeys([]);
  };

  const openCreateChargeModal = (e) => {
    hideButtonVisitMark(e);
    setVisibleCreateChargeModal(true);
  };

  const closeCreateChargeModal = () => {
    setVisibleCreateChargeModal(false);
  };

  const openCreateAdjustmentModal = (e) => {
    hideButtonVisitMark(e);
    setVisibleCreateAdjustmentModal(true);
  };

  const closeCreateAdjustmentModal = () => {
    setVisibleCreateAdjustmentModal(false);
  };

  const openConfirmCreateAdjustmentModal = () => {
    setVisibleConfirmCreateAdjustmentModal(true);
  };

  const closeConfirmCreateAdjustmentModal = () => {
    setVisibleConfirmCreateAdjustmentModal(false);
  };

  const handleShowModal = (objId) => {
    setObject(objId);
  };

  const handleHideModal = () => {
    setObject("");
  };

  const handleCreateCharge = (values) => {
    const createData = {
      agreementId: values.agreementId,
      serviceIds: values.serviceIds,
      periodTo: toHttpFormatEndOfDay(values.periodTo),
    };
    if (values.periodFrom) {
      createData.periodFrom = toHttpFormatStartOfDay(values.periodFrom);
    }
    closeCreateChargeModal();
    dispatch(createCharge(createData)).finally(() => {
      handleSearchChargesAndResetSelectedRowKeys();
    });
  };

  const handleCreateAdjustment = () => {
    formCreateAdjustment
      .validateFields()
      .then((values) => {
        dispatch(
          createUpdateAdjustment({
            agreementId: values?.agreementId,
            adjustmentTypeId: 1,
            queryString: buildQueryStringWithObjectParams({
              serviceId: values?.serviceId || null,
              amount: Number(values?.amount),
              dsc: values?.dsc || null,
              chargeName: values?.chargeName || null,
              periodFrom: toHttpFormatStartOfDay(values.period[0]),
              periodTo: toHttpFormatEndOfDay(values.period[1]),
            }),
          })
        )
          .then(() => {
            dispatch(fetchChargesByAggId(chargeParams));
          })
          .finally(() => {
            closeConfirmCreateAdjustmentModal();
            closeCreateAdjustmentModal();
          });
      })
      .catch((errorInfo) => {
        console.log(errorInfo);
      });
  };

  const checkHideColumns = (name) => {
    let check = true;
    const findIndex =
      localStore?.chargeColumns &&
      localStore?.chargeColumns.findIndex((item) => item.title === name);
    if (findIndex !== -1) {
      check = false;
    }
    return check;
  };

  const columnsWidths = {
    chargeId: "80px",
    agreementId: "70px",
    serviceTypeName: "140px",
    rateMode: "140px",
    offeringName: "140px",
    rateTypeId: "140px",
    period: "100px",
    chargeUnits: "90px",
    chargeAmount: "85px",
    chargePrice: "85px",
    chargeName: "140px",
    adjustmentTypeId: "110px",
    invoiceExtNum: "100px",
    billNumber: "100px",
    invoicePayStatus: "100px",
    invoiceAmount: "85px",
    invoicePeriod: "100px",
    invoiceDate: "85px",
    billPayStatus: "100px",
    billAmount: "85px",
    billPeriod: "100px",
    billFromDate: "85px",
    buttons: 50,
    invoiceId: "85px",
  };

  const [columns, setColumns] = useState([
    {
      title: "ID начисления",
      dataIndex: "chargeId",
      key: "chargeId",
      hidden: true,
      width: columnsWidths.chargeId,
      render: (chargeId) => (
        <span style={{ color: "rgba(0, 0, 0, 0.3)", fontWeight: "normal" }}>{chargeId}</span>
      ),
    },
    {
      title: "ID договора",
      dataIndex: "agreementId",
      key: "agreementId",
      width: columnsWidths.agreementId,
      render: (agreementId) => (
        <span style={{ color: "rgba(0, 0, 0, 0.3)", fontWeight: "normal" }}>{agreementId}</span>
      ),
      hidden: true,
    },
    {
      title: "Тип продукта",
      dataIndex: "serviceTypeName",
      key: "serviceTypeName",
      hidden: localStore?.chargeColumns ? checkHideColumns("Тип продукта") : false,
      width: columnsWidths.serviceTypeName,
    },
    {
      title: "Вид цены",
      dataIndex: "rateMode",
      key: "rateMode",
      hidden: localStore?.chargeColumns ? checkHideColumns("Вид цены") : true,
      width: columnsWidths.rateMode,
    },
    {
      title: "Предложение",
      dataIndex: "offeringName",
      key: "offeringName",
      hidden: localStore?.chargeColumns ? checkHideColumns("Предложение") : false,
      width: columnsWidths.offeringName,
    },
    {
      title: "Тип цены",
      dataIndex: "rateTypeId",
      key: "rateTypeId",
      hidden: localStore?.chargeColumns ? checkHideColumns("Тип цены") : false,
      width: columnsWidths.rateTypeId,
    },
    {
      title: "Период",
      dataIndex: "period",
      key: "period",
      hidden: localStore?.chargeColumns ? checkHideColumns("Период") : false,
      render: (period) => (
        <>
          {period.map((item) => (
            <span key={item.id}>
              {item.date}
              {`${item.id === 1 ? " - " : ""} `}
            </span>
          ))}
        </>
      ),
      width: columnsWidths.period,
    },
    {
      title: "Цена",
      dataIndex: "chargePrice",
      key: "chargePrice",
      hidden: localStore?.chargeColumns ? checkHideColumns("Цена") : false,
      render: (chargePrice) => SpanNumberFormat({ amount: chargePrice }),
      width: columnsWidths.chargePrice,
    },
    {
      title: "Количество",
      dataIndex: "chargeUnits",
      key: "chargeUnits",
      hidden: localStore?.chargeColumns ? checkHideColumns("Количество") : false,
      render: (chargeUnits) => SpanNumberFormat({ amount: chargeUnits, fractSize: 0 }),
      width: columnsWidths.chargeUnits,
    },
    {
      title: "Сумма",
      dataIndex: "chargeAmount",
      key: "chargeAmount",
      hidden: localStore?.chargeColumns ? checkHideColumns("Сумма") : false,
      render: (chargeAmount) => SpanNumberFormat({ amount: chargeAmount }),
      width: columnsWidths.chargeAmount,
    },
    {
      title: "Номенклатура",
      dataIndex: "chargeName",
      key: "chargeName",
      hidden: localStore?.chargeColumns ? checkHideColumns("Номенклатура") : true,
      render: (chargeName) => <Tooltip title={chargeName}>{limitText(chargeName, 30)}</Tooltip>,
      width: columnsWidths.chargeName,
    },
    {
      title: "Начисление/Корректировка",
      dataIndex: "adjustmentTypeId",
      key: "adjustmentTypeId",
      hidden: localStore?.chargeColumns ? checkHideColumns("Начисление/Корректировка") : false,
      width: columnsWidths.adjustmentTypeId,
    },
    {
      title: "ID инвойса",
      dataIndex: "invoiceId",
      key: "invoiceId",
      hidden: localStore?.chargeColumns ? checkHideColumns("ID инвойса") : true,
      width: columnsWidths.invoiceId,
    },
    {
      title: "Номер СФ",
      dataIndex: "invoiceExtNum",
      key: "invoiceExtNum",
      hidden: localStore?.chargeColumns ? checkHideColumns("Номер СФ") : false,
      width: columnsWidths.invoiceExtNum,
    },
    {
      title: "Номер счета",
      dataIndex: "billNumber",
      key: "billNumber",
      hidden: localStore?.chargeColumns ? checkHideColumns("Номер счета") : false,
      width: columnsWidths.billNumber,
    },
    {
      title: "Статус СФ",
      dataIndex: "invoicePayStatus",
      key: "invoicePayStatus",
      hidden: localStore?.chargeColumns ? checkHideColumns("Статус СФ") : true,
      width: columnsWidths.invoicePayStatus,
    },
    {
      title: "Сумма СФ",
      dataIndex: "invoiceAmount",
      key: "invoiceAmount",
      hidden: localStore?.chargeColumns ? checkHideColumns("Сумма СФ") : true,
      render: (invoiceAmount) => SpanNumberFormat({ amount: invoiceAmount }),
      width: columnsWidths.invoiceAmount,
    },
    {
      title: "Период СФ",
      dataIndex: "invoicePeriod",
      key: "invoicePeriod",
      hidden: localStore?.chargeColumns ? checkHideColumns("Период СФ") : true,
      render: (invoicePeriod) => (
        <>
          {invoicePeriod.map((item) => (
            <span key={item.id}>
              {item.date}
              {`${item.id === 1 ? " - " : ""} `}
            </span>
          ))}
        </>
      ),
      width: columnsWidths.invoicePeriod,
    },
    {
      title: "Дата СФ",
      dataIndex: "invoiceDate",
      key: "invoiceDate",
      hidden: localStore?.chargeColumns ? checkHideColumns("Дата СФ") : true,
      width: columnsWidths.invoiceDate,
    },
    {
      title: "Статус оплаты счета",
      dataIndex: "billPayStatus",
      key: "billPayStatus",
      hidden: localStore?.chargeColumns ? checkHideColumns("Статус оплаты счета") : true,
      width: columnsWidths.billPayStatus,
    },
    {
      title: "Сумма счета",
      dataIndex: "billAmount",
      key: "billAmount",
      hidden: localStore?.chargeColumns ? checkHideColumns("Сумма счета") : true,
      render: (billAmount) => SpanNumberFormat({ amount: billAmount }),
      width: columnsWidths.billAmount,
    },
    {
      title: "Период счета",
      dataIndex: "billPeriod",
      key: "billPeriod",
      hidden: localStore?.chargeColumns ? checkHideColumns("Период счета") : true,
      render: (billPeriod) => (
        <>
          {billPeriod.map((item) => (
            <span key={item.id}>
              {item.date}
              {`${item.id === 1 ? " - " : ""} `}
            </span>
          ))}
        </>
      ),
      width: columnsWidths.billPeriod,
    },
    {
      title: "Дата счета",
      dataIndex: "billFromDate",
      key: "billFromDate",
      hidden: localStore?.chargeColumns ? checkHideColumns("Дата счета") : true,
      width: columnsWidths.billFromDate,
    },
    {
      title: "",
      dataIndex: "",
      key: "x",
      hidden: false,
      render: (record) => (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            handleShowModal(record);
          }}
          className="button-icon"
          style={{ outline: "none" }}
          icon={<DotsIcon />}
        />
      ),
      width: columnsWidths.buttons,
    },
  ]);

  const filterColumns = [...columns].filter((item) => !item.hidden);

  let tableData;

  if (checkIsLoaded(chargesLoading) && filterColumns && filterColumns.length) {
    tableData =
      data &&
      data.map((item) => ({
        key: item.serialNumber,
        agreementId: item.agreementId,
        rateTypeId:
          item.rateTypeId && checkIsLoaded(rateTypeLoading) && rateType
            ? rateType.find((findItem) => findItem?.id === item.rateTypeId)?.name
            : "-",
        serviceTypeName:
          !item.serviceTypeName && item.isAdjustment === 1
            ? item?.chargeName
            : item?.serviceTypeName,
        rateMode: item.rateMode,
        offeringName: item.offeringName,
        period: [
          {
            id: 1,
            date: item.chargePeriodFrom && toDisplayFormat(item.chargePeriodFrom),
          },
          {
            id: 2,
            date: item.chargePeriodTo && toDisplayFormat(item.chargePeriodTo),
          },
        ],
        chargeUnits: item.chargeUnits,
        chargeAmount: item?.chargeAmount && round(item?.chargeAmount),
        chargeName: item.chargeName,
        adjustmentTypeId:
          Number(item.isAdjustment) === 0 ? (
            <Tooltip title="Начисление">{chargeIcon()}</Tooltip>
          ) : (
            <Tooltip title="Корректировка">{adjustmentIcon()}</Tooltip>
          ),
        invoiceExtNum: item.invoiceExtNum,
        billNumber: item.billNumber,
        invoicePayStatus: item.invoicePayStatus,
        invoiceAmount: item?.invoiceAmount && round(item?.invoiceAmount),
        invoicePeriod: [
          {
            id: 1,
            date: item.invoicePeriodFrom && toDisplayFormat(item.invoicePeriodFrom),
          },
          {
            id: 2,
            date: item.invoicePeriodTo && toDisplayFormat(item.invoicePeriodTo),
          },
        ],
        invoiceDate: item.invoiceDate && toDisplayFormat(item.invoiceDate),
        billPayStatus: item.billPayStatus,
        billAmount: item?.billAmount && round(item?.billAmount),
        billPeriod: [
          {
            id: 1,
            date: item.billPeriodFrom && toDisplayFormat(item.billPeriodFrom),
          },
          {
            id: 2,
            date: item.billPeriodTo && toDisplayFormat(item.billPeriodTo),
          },
        ],
        billFromDate: item.billFromDate && toDisplayFormat(item.billFromDate),
        chargeId: item.chargeId,
        chargePeriodFrom: item.chargePeriodFrom,
        chargePeriodTo: item.chargePeriodTo,
        isAdjustment: item.isAdjustment,
        invoiceId: item?.invoiceId,
        chargePrice: item?.chargePrice,
      }));
  }

  const columnKeyList = useMemo(() => filterColumns.map(({ key }) => key), [filterColumns]);

  const xlsExportHeaders = useMemo(() => {
    const headerList = [
      { name: "agreementId", title: "Договор", type: "String" },
      { name: "serviceTypeName", title: "Тип продукта", type: "String" },
      { name: "rateMode", title: "Вид цены", type: "String" },
      { name: "offeringName", title: "Предложение", type: "String" },
      { name: "rateTypeId", title: "Тип цены", type: "String" },
      { name: "period", title: "Период", type: "String" },
      { name: "chargePrice", title: "Цена", type: "Double" },
      { name: "chargeUnits", title: "Количество", type: "Double" },
      { name: "chargeAmount", title: "Сумма", type: "Double" },
      { name: "chargeName", title: "Номенклатура", type: "String" },
      {
        name: "adjustmentTypeId",
        title: "Начисление/Корректировка",
        type: "String",
      },
      { name: "invoiceId", title: "ID инвойса", type: "String" },
      { name: "invoiceExtNum", title: "Номер СФ", type: "String" },
      { name: "billNumber", title: "Номер счета", type: "String" },
      { name: "invoicePayStatus", title: "Статус СФ", type: "String" },
      { name: "invoiceAmount", title: "Сумма СФ", type: "String" },
      { name: "invoicePeriod", title: "Период СФ", type: "String" },
      { name: "invoiceDate", title: "Дата СФ", type: "String" },
      {
        name: "billPayStatus",
        title: "Статус оплаты счета",
        type: "String",
      },
      { name: "billAmount", title: "Сумма счета", type: "Double" },
      { name: "billPeriod", title: "Период счета", type: "String" },
      { name: "billFromDate", title: "Дата счета", type: "String" },
    ];
    return headerList.filter(({ name }) => columnKeyList.includes(name));
  }, [columnKeyList]);

  const handleChangeCreateInvoiceModalData = (values) => {
    setCreateInvoiceModalData((prev) => ({
      ...prev,
      ...values,
    }));
  };

  const openCreateInvoiceModal = (e) => {
    hideButtonVisitMark(e);
    const selectedPayments =
      data && data.filter((item) => selectedRowKeys.includes(item.serialNumber));
    const invoiceIdNullPayments = selectedPayments.filter(({ invoiceId }) => invoiceId === null);
    setCreateInvoiceModalData({
      visible: true,
      isDeletedAccountedInvoices: selectedPayments.length !== invoiceIdNullPayments.length,
      invoiceIdNullPayments,
      effectiveDate: moment(),
    });
  };

  const closeCreateInvoiceModal = () => {
    setCreateInvoiceModalData({ visible: false });
  };

  const submitCreateInvoiceModal = () => {
    startLoading();
    const createdInvoices = createInvoiceModalData.invoiceIdNullPayments.reduce((acc, item) => {
      const createdInvoice = acc.find(({ agreementId }) => agreementId === item.agreementId);
      const periodTo = moment(item.chargePeriodTo).endOf("month");
      if (createdInvoice) {
        createdInvoice.transactionId.push(item.chargeId);
        if (moment(createdInvoice.periodTo).isBefore(periodTo)) createdInvoice.periodTo = periodTo;
      } else {
        acc.push({
          agreementId: item.agreementId,
          transactionId: [item.chargeId],
          periodTo,
        });
      }
      return acc;
    }, []);
    const bodyRequestArr = createdInvoices.map((item) => ({
      ...item,
      transactionId: uniq(item.transactionId),
      periodTo: formatDate.toHttpFormat(item.periodTo),
      excludeInvoicePeriod: [],
      effectiveDate: formatDate.toHttpFormatStartOfDay(createInvoiceModalData.effectiveDate),
    }));
    dispatch(createInvoices(bodyRequestArr))
      .then(() => {
        handleSearchChargesAndResetSelectedRowKeys();
        closeCreateInvoiceModal();
      })
      .finally(stopLoading);
  };

  const openCreateBillModal = async (e) => {
    hideButtonVisitMark(e);
    const selectedPayments =
      data && data.filter((item) => selectedRowKeys.includes(item.serialNumber));

    const invoiceNotNullPayments = selectedPayments.filter(({ invoiceId }) => invoiceId !== null);

    const invoiceNullPayments = selectedPayments.filter(({ invoiceId }) => invoiceId === null);

    const billIdNullPayments = invoiceNotNullPayments.filter(({ billId }) => billId === null);

    const isMixingInvoices =
      invoiceNotNullPayments.length !== 0 && invoiceNullPayments.length !== 0;
    const isAllNullInvoices = invoiceNotNullPayments.length === 0 && invoiceNullPayments.length > 0;

    if (isAllNullInvoices) {
      const transactionIds = selectedPayments.map(({ chargeId }) => chargeId);
      const serviceIds = selectedPayments.map(({ serviceId }) => serviceId);

      let isFindPaymentTypeWarning = false;

      for (let i = 0; i < serviceIds.length; i++) {
        const findService = serviceForAdjustmentCreate?.find(
          (service) => service.id === serviceIds[i]
        );

        if (findService) {
          const findPaymentTypeId = findService?.serviceProfile?.paymentTypeId;
          if (findPaymentTypeId === 1) {
            isFindPaymentTypeWarning = true;
            setCreateBillModalData({
              visible: true,
              isMixingInvoices,
              isAllNullInvoices,
              isDeletedAccountedInvoices: selectedPayments.length !== billIdNullPayments.length,
              billIdNullPayments,
              invoiceNullPayments,
              isFindPaymentTypeWarning: true,
              onDate: moment(),
            });
            break; // Прервать цикл при условии что findPaymentTypeId === 1
          }
        } else {
          // Если среди изначано загруженных продуктов отсутствует такой serviceId, то делаем запрос для поиска этого продукта по id
          try {
            const url = getRoute(
              "services-api",
              "services",
              `${serviceIds[i]}`,
              "versions",
              "last"
            );
            const response = axios.get(url);
            const { data } = await response;

            if (data?.serviceProfile?.paymentTypeId === 1) {
              isFindPaymentTypeWarning = true;
              setCreateBillModalData({
                visible: true,
                isMixingInvoices,
                isAllNullInvoices,
                isDeletedAccountedInvoices: selectedPayments.length !== billIdNullPayments.length,
                billIdNullPayments,
                invoiceNullPayments,
                isFindPaymentTypeWarning: true,
                onDate: moment(),
              });
              break; // Прервать цикл при условии что findPaymentTypeId === 1
            }
          } catch (e) {
            message.destroy();
            Message("Ошибка загрузки информации о продукте. Попробуйте позже", e);
          }
        }
      }

      !isFindPaymentTypeWarning &&
        setCreateBillModalData({
          visible: true,
          isMixingInvoices,
          isAllNullInvoices,
          isDeletedAccountedInvoices: selectedPayments.length !== billIdNullPayments.length,
          billIdNullPayments,
          invoiceNullPayments,
          isFindPaymentTypeWarning,
          transactionIds,
          onDate: moment(),
        });
    } else {
      setCreateBillModalData({
        visible: true,
        isMixingInvoices,
        isAllNullInvoices,
        isDeletedAccountedInvoices: selectedPayments.length !== billIdNullPayments.length,
        billIdNullPayments,
        invoiceNullPayments,
        onDate: moment(),
      });
    }
  };

  const closeCreateBillModal = () => {
    setCreateBillModalData({ visible: false });
  };

  const handleChangeCreateBillModalData = (values) => {
    setCreateBillModalData((prev) => ({
      ...prev,
      ...values,
    }));
  };

  const submitCreateBillModal = () => {
    startLoading();
    const createdBills = createBillModalData.billIdNullPayments.reduce((acc, item) => {
      const createdBill = acc.find(({ agreementId }) => agreementId === item.agreementId);
      const periodTo = moment(item.chargePeriodTo).endOf("month");
      if (createdBill) {
        createdBill.filtredInvoiceIds.push(item.invoiceId);
        if (moment(createdBill.periodTo).isBefore(periodTo)) createdBill.periodTo = periodTo;
      } else {
        acc.push({
          agreementId: item.agreementId,
          filtredInvoiceIds: [item.invoiceId],
          periodTo,
        });
      }
      return acc;
    }, []);
    const bodyRequestArr = createdBills.map((item) => ({
      ...item,
      filtredInvoiceIds: uniq(item.filtredInvoiceIds),
      periodTo: formatDate.toHttpFormat(item.periodTo),
      onDate: formatDate.toHttpFormat(createBillModalData.onDate),
    }));
    dispatch(createPostBill({ bodyRequestArr }))
      .then(() => {
        handleSearchChargesAndResetSelectedRowKeys();
        closeCreateBillModal();
      })
      .finally(stopLoading);
  };

  const submitCreateBillModalForAllNullInvoces = () => {
    startLoading();
    const createdBills = createBillModalData.invoiceNullPayments.reduce((acc, item) => {
      const createdBill = acc.find(({ agreementId }) => agreementId === item.agreementId);
      const periodTo = moment(item.chargePeriodTo).endOf("month");
      if (createdBill) {
        createdBill.transactionIds.push(item.chargeId);
        if (moment(createdBill.periodTo).isBefore(periodTo)) createdBill.periodTo = periodTo;
      } else {
        acc.push({
          agreementId: item.agreementId,
          transactionIds: [item.chargeId],
          periodTo,
        });
      }
      return acc;
    }, []);
    const bodyRequestArr = createdBills.map((item) => ({
      ...item,
      filtredInvoiceIds: uniq(item.filtredInvoiceIds),
      periodTo: formatDate.toHttpFormat(item.periodTo),
      onDate: formatDate.toHttpFormat(createBillModalData.onDate),
    }));
    dispatch(createPostBill({ bodyRequestArr }))
      .then(() => {
        handleSearchChargesAndResetSelectedRowKeys();
        closeCreateBillModal();
      })
      .finally(startLoading);
  };

  const renderFooter = () => {
    const handleExport = async (payments) => {
      const { display_name: author } = jwtDecode(AuthService.getCurrentUser());
      const exportedData = {
        fileName: `Детализация аккаунта_${formatDate.toDisplayFormat(moment())}`,
        title: "Выгрузка данных в формате xls",
        description: "Выгрузка данных",
        author,
        header: xlsExportHeaders,
        data:
          payments &&
          payments.map((detail) => {
            const period = [
              detail.chargePeriodFrom ? toDisplayFormat(detail.chargePeriodFrom) : "",
              detail.chargePeriodTo ? toDisplayFormat(detail.chargePeriodTo) : "",
            ].join(" - ");
            const invoicePeriod = [
              detail.invoicePeriodFrom ? toDisplayFormat(detail.invoicePeriodFrom) : "",
              detail.invoicePeriodTo ? toDisplayFormat(detail.invoicePeriodTo) : "",
            ].join(" - ");
            const billPeriod = [
              detail.billPeriodFrom ? toDisplayFormat(detail.billPeriodFrom) : "",
              detail.billPeriodTo ? toDisplayFormat(detail.billPeriodTo) : "",
            ].join(" - ");
            return pick(
              {
                agreementId: detail.agreementId,
                serviceTypeName:
                  !detail?.serviceTypeName && detail.isAdjustment === 1
                    ? detail?.chargeName
                    : detail?.serviceTypeName,
                rateMode: detail?.rateMode,
                offeringName: detail?.offeringName,
                rateTypeId:
                  detail.rateTypeId && checkIsLoaded(rateTypeLoading) && rateType
                    ? rateType.find((findItem) => findItem?.id === detail.rateTypeId)?.name
                    : "-",
                period,
                chargePrice: detail?.chargePrice,
                chargeUnits: detail?.chargeUnits,
                chargeAmount: detail?.chargeAmount && round(detail?.chargeAmount),
                chargeName: detail?.chargeName,
                adjustmentTypeId:
                  Number(detail?.adjustmentTypeId) === 0 ? "Начисление" : "Корректировка",
                invoiceId: detail?.invoiceId,
                invoiceExtNum: detail?.invoiceExtNum,
                billNumber: detail?.billNumber,
                invoicePayStatus: detail?.invoicePayStatus,
                invoiceAmount: detail?.invoiceAmount && round(detail?.invoiceAmount),
                invoicePeriod,
                invoiceDate: detail?.invoiceDate ? toDisplayFormat(detail?.invoiceDate) : "",
                billPayStatus: detail?.billPayStatus,
                billAmount: detail?.billAmount && round(detail?.billAmount),
                billPeriod,
                billFromDate: detail?.billFromDate ? toDisplayFormat(detail?.billFromDate) : "",
              },
              columnKeyList
            );
          }),
      };
      dispatch(exportToXLS(exportedData)).finally(stopLoading);
    };

    const handleExportSelected = (e) => {
      hideButtonVisitMark(e);
      startLoading();
      const selectedPayments =
        (data && data.filter((item) => selectedRowKeys.includes(item.serialNumber))) || [];
      handleExport(selectedPayments);
    };

    const handleExportAll = (e) => {
      hideButtonVisitMark(e);
      startLoading();
      fetchChargesAll()
        .then((paymentsAll) => {
          handleExport(paymentsAll);
        })
        .catch(stopLoading);
    };

    return (
      <Space className="buttons-container">
        <Button
          type="primary"
          disabled={selectedRowKeys.length === 0 || !filterColumns.length}
          onClick={openCreateInvoiceModal}
        >
          Создать инвойсы (счета-фактуры)
        </Button>
        <Button
          style={{ marginLeft: "15px" }}
          type="primary"
          disabled={selectedRowKeys.length === 0 || !filterColumns.length}
          onClick={openCreateBillModal}
        >
          Создать счет на постоплату
        </Button>
        <Button
          style={{ marginLeft: "15px" }}
          type="primary"
          disabled={selectedRowKeys.length === 0 || !filterColumns.length}
          loading={isLoading}
          onClick={handleExportSelected}
        >
          Выгрузить в XLS (выбранное)
        </Button>
        <Button
          style={{ marginLeft: "15px" }}
          type="primary"
          disabled={isEmpty(tableData) || !filterColumns.length}
          loading={isLoading}
          onClick={handleExportAll}
        >
          Выгрузить в XLS (все)
        </Button>
      </Space>
    );
  };

  const getTooltipTitle = (item) => {
    const cellStyle = {
      border: `1px solid rgba(0, 0, 0, 0.1)`,
      padding: "5px",
      color: "rgba(0, 0, 0, 0.3)",
      backgroundColor: "white",
    };
    return (
      <div style={{ display: "flex" }}>
        <div>
          {/* <div style={headerStyle}>Тип продукта</div> */}
          <div style={cellStyle}>{item.serviceType.name}</div>
        </div>
        {item.offering?.extName ? (
          <div>
            <div style={cellStyle}>{item.offering?.extName}</div>
          </div>
        ) : null}
        <div>
          {/* <div style={headerStyle}>Статус</div> */}
          <div style={cellStyle}>{find(serviceStatus, { id: item.serviceStatusId })?.name}</div>
        </div>
        <div>
          {/* <div style={headerStyle}>С</div> */}
          <div style={cellStyle}>{moment(item.fromDate).format(displayFormat)}</div>
        </div>
        <div>
          {/* <div style={headerStyle}>До</div> */}
          <div style={cellStyle}>{moment(item.toDate).format(displayFormat)}</div>
        </div>
        <div>
          {/* <div style={headerStyle}>id</div> */}
          <div style={cellStyle}>{item.id}</div>
        </div>
      </div>
    );
  };

  const onSearch = (values) => {
    if (values) {
      values.accountId = accountId;
      values.page = 0;
      values.size = chargeParams.size;
      values.chargeDateFrom = toHttpFormatStartOfDay(values.chargeDate[0]);
      values.chargeDateTo = toHttpFormatEndOfDay(values.chargeDate[1]);
      values.chargePeriodFrom = toHttpFormatStartOfDay(values.chargePeriod[0]);
      values.chargePeriodTo = toHttpFormatEndOfDay(values.chargePeriod[1]);

      values.invoiceDateFrom =
        values.invoiceDate && values.invoiceDate[0]
          ? toHttpFormatStartOfDay(values.invoiceDate[0])
          : "";
      values.invoiceDateTo =
        values.invoiceDate && values.invoiceDate[1]
          ? toHttpFormatEndOfDay(values.invoiceDate[1])
          : "";

      values.billDateFrom =
        values.billDate && values.billDate[0] ? toHttpFormatStartOfDay(values.billDate[0]) : "";
      values.billDateTo =
        values.billDate && values.billDate[1] ? toHttpFormatEndOfDay(values.billDate[1]) : "";

      values.billPeriodFrom =
        values.billPeriod && values.billPeriod[0]
          ? toHttpFormatStartOfDay(values.billPeriod[0])
          : "";
      values.billPeriodTo =
        values.billPeriod && values.billPeriod[1] ? toHttpFormatEndOfDay(values.billPeriod[1]) : "";

      values.chargeIds = values.chargeIds
        ? values.chargeIds.split(/\D/).filter(Boolean).map(Number)
        : "";

      values.chargeDate = "";
      values.chargePeriod = "";
      values.invoiceDate = "";
      values.billDate = "";
      values.billPeriod = "";
      values.invoiceId = values.invoiceId ? values.invoiceId : "";
    }
    setSelectedRowKeys([]);
    handleSearchCharges(values);
  };

  return (
    <>
      <div>
        <div className="charges-filters">
          <Row justify="space-between" align="middle">
            <Col>
              <h1>Начисления</h1>
            </Col>
            <Col align="end">
              {hasAccess(MENU_ACCOUNT_CHARGES_WRITE) && (
                <div style={{ display: "flex" }}>
                  <Button
                    onClick={openCreateChargeModal}
                    type="primary"
                    style={{ marginRight: "15px" }}
                  >
                    Создать начисление
                  </Button>
                  <Button onClick={openCreateAdjustmentModal} type="primary">
                    Создать корректировку
                  </Button>
                </div>
              )}
            </Col>
          </Row>

          <AccountChargesSearchFilters
            defaultSearchParams={chargesSearchParams}
            form={formFilters}
            onSearch={onSearch}
            resetSearchPaymentsAndResetSetSelectedRowKeys={
              resetSearchPaymentsAndResetSetSelectedRowKeys
            }
            filtersCollapseState={filtersCollapseState}
            setFiltersCollapseState={setFiltersCollapseState}
          />
        </div>
        <Row justify="space-between" align="middle" className=" account-detalization__pagination">
          <Col span={8}>
            <Row align="middle">
              <Col>
                <Checkbox
                  onChange={toggleZeroCharges}
                  style={{ color: "#5D6C7A" }}
                  checked={checked}
                >
                  Скрывать нулевые начисления
                </Checkbox>
              </Col>
            </Row>
          </Col>
          <Col span={8} offset={2}>
            <Row justify="flex-start">
              <Pagination
                onChange={(e) => handlePageSize("page", e)}
                current={getValueChargesPagingAndFilters("page") + 1}
                total={chargesData && chargesData?.totalElements}
                pageSize={getValueChargesPagingAndFilters("size")}
                hideOnSinglePage
                size="small"
                showSizeChanger={false}
              />
            </Row>
          </Col>
          <Col span={6} style={{ paddingRight: "5px" }}>
            <Row justify="end" align="middle" wrap={false}>
              <ToggleColumns
                tableName="account-charges-table"
                columns={columns}
                setColumns={setColumns}
              />
            </Row>
          </Col>
        </Row>
      </div>

      <div className="charges-table">
        {filterColumns && (
          <Table
            size="small"
            columns={filterColumns}
            loading={checkIsLoading(chargesLoading)}
            dataSource={tableData}
            pagination={false}
            scroll={{ y: getTableHeight(), x: true }}
            rowSelection={{
              selectedRowKeys,
              onChange: (selectedKeys) => {
                setSelectedRowKeys(selectedKeys);
              },
            }}
            onRow={(record) => ({
              onClick: (e) => {
                e.stopPropagation();
                sessionStorage.setItem(
                  "chargeFilters",
                  JSON.stringify({
                    accountId,
                    path: url,
                    chargeParams,
                    chargeColumns,
                  })
                );
                history.push(`/charges-details/${record.chargeId}`);
              },
            })}
          />
        )}
      </div>

      <CreateChargeModal
        visible={visibleCreateChargeModal}
        onClose={closeCreateChargeModal}
        handleCreateCharge={handleCreateCharge}
        isServicesLoading={isServicesLoading}
        defaultSelectedStatuses={defaultSelectedStatuses}
        setLoadServicesData={setLoadServicesData}
        agreements={agreements}
        agrStatus={agrStatus}
        serviceForAdjustmentCreate={serviceForAdjustmentCreate}
        getTooltipTitle={getTooltipTitle}
      />

      <CreateAdjustmentModal
        visible={visibleCreateAdjustmentModal}
        onClose={closeCreateAdjustmentModal}
        onSubmit={openConfirmCreateAdjustmentModal}
        agreements={agreements}
        form={formCreateAdjustment}
        setLoadServicesData={setLoadServicesData}
        agrStatus={agrStatus}
        isServicesLoading={isServicesLoading}
        defaultSelectedStatuses={defaultSelectedStatuses}
        getTooltipTitle={getTooltipTitle}
        serviceForAdjustmentCreate={serviceForAdjustmentCreate}
      />

      <Modal
        onCancel={closeConfirmCreateAdjustmentModal}
        className="modal-charge-confirm"
        visible={visibleConfirmCreateAdjustmentModal}
        width={500}
        footer={[
          <Button key="1" onClick={closeConfirmCreateAdjustmentModal}>
            Отмена
          </Button>,
          <Button
            key="2"
            loading={checkIsLoading(createUpdateAdjustmentLoading)}
            onClick={handleCreateAdjustment}
            type="primary"
          >
            Подтвердить
          </Button>,
        ]}
      >
        <div style={{ textAlign: "center", marginTop: "20px" }}>
          <span style={{ fontSize: "16px" }}>Вы подтверждаете создание корректировки?</span>
        </div>
      </Modal>

      <Modal
        onCancel={closeCreateInvoiceModal}
        className="modal-charge-confirm"
        visible={
          createInvoiceModalData.visible && !createInvoiceModalData.invoiceIdNullPayments.length
        }
        width={500}
        title="Создание инвойсов"
        footer={[
          <Button
            key="1"
            onClick={closeCreateInvoiceModal}
            style={{ width: "107px" }}
            type="primary"
          >
            Ок
          </Button>,
        ]}
      >
        <div
          style={{
            fontSize: "14px",
            display: "flex",
            marginLeft: "15px",
          }}
        >
          <div>{warningIcon}</div>
          <span style={{ paddingLeft: "10px", paddingTop: "2px" }}>
            Начисления, доступные для создания инвойсов, отсутствуют.
          </span>
        </div>
      </Modal>

      <Modal
        onCancel={closeCreateInvoiceModal}
        className="modal-charge-confirm"
        visible={
          createInvoiceModalData.visible && createInvoiceModalData.invoiceIdNullPayments.length
        }
        width={500}
        title="Создание инвойсов"
        footer={[
          <Button
            key="1"
            onClick={closeCreateInvoiceModal}
            style={{
              width: "107px",
            }}
          >
            Отмена
          </Button>,
          <Button
            key="2"
            onClick={submitCreateInvoiceModal}
            style={{ width: "127px" }}
            type="primary"
            loading={isLoading}
          >
            Продолжить
          </Button>,
        ]}
      >
        <div style={{ textAlign: "center", fontSize: "14px" }}>
          {createInvoiceModalData.isDeletedAccountedInvoices && (
            <div style={{ display: "flex", marginLeft: "10px", marginBottom: "15px" }}>
              <div>{warningIcon}</div>
              <span style={{ paddingLeft: "15px", paddingTop: "2px" }}>
                Начисления, учтенные в инвойсах, были удалены из списка.
              </span>
            </div>
          )}
          <div>
            <label style={{ paddingRight: "15px" }}>Дата счета-фактуры</label>
            <DatePicker
              allowClear={false}
              format={displayFormat}
              value={createInvoiceModalData.effectiveDate}
              onChange={(date) => {
                handleChangeCreateInvoiceModalData({
                  effectiveDate: date,
                });
              }}
            />
          </div>
        </div>
      </Modal>

      <Modal
        onCancel={closeCreateBillModal}
        className="modal-charge-confirm"
        visible={createBillModalData.visible && createBillModalData.isAllNullInvoices}
        width={750}
        title="Создание счета на постоплату"
        footer={[
          <Button
            key="1"
            onClick={closeCreateBillModal}
            style={{
              width: "107px",
            }}
          >
            Отмена
          </Button>,
          <Button
            key="2"
            onClick={submitCreateBillModalForAllNullInvoces}
            style={{ width: "127px" }}
            type="primary"
            loading={isLoading}
          >
            Продолжить
          </Button>,
        ]}
      >
        <div style={{ textAlign: "center", fontSize: "14px" }}>
          {createBillModalData.isFindPaymentTypeWarning ? (
            <div
              style={{
                display: "flex",
                marginLeft: "20px",
                marginBottom: "15px",
                alignItems: "center",
              }}
            >
              <div>{warningIcon}</div>
              <span style={{ paddingLeft: "15px", paddingTop: "2px" }}>
                Среди выбранных начислений есть начисления по продуктам с профилем оплаты
                предоплата.
                <br />
                Вы подтверждаете создание счета-фактуры и внеочередного счета на оплату?
              </span>
            </div>
          ) : (
            <div style={{ display: "flex", marginBottom: "15px", justifyContent: "center" }}>
              <span style={{ paddingLeft: "15px", paddingTop: "2px" }}>
                Вы подтверждаете создание счета-фактуры и внеочередного счета на оплату?
              </span>
            </div>
          )}
          <div>
            <label style={{ paddingRight: "15px" }}>Дата счета</label>
            <DatePicker
              allowClear={false}
              format={displayFormat}
              value={createBillModalData.onDate}
              onChange={(date) => {
                handleChangeCreateBillModalData({
                  onDate: date,
                });
              }}
            />
          </div>
        </div>
      </Modal>

      <Modal
        onCancel={closeCreateBillModal}
        className="modal-charge-confirm"
        visible={createBillModalData.visible && createBillModalData.isMixingInvoices}
        width={500}
        title="Создание счета на постоплату"
        footer={[
          <Button key="2" onClick={closeCreateBillModal} style={{ width: "107px" }} type="primary">
            Ок
          </Button>,
        ]}
      >
        <div
          style={{
            fontSize: "14px",
            display: "flex",
            marginLeft: "15px",
          }}
        >
          <div>{warningIcon}</div>
          <span style={{ paddingLeft: "10px", paddingTop: "2px" }}>
            Для части начислений уже сформирован счет-фактура. Невозможно выполнить данную операцию.
          </span>
        </div>
      </Modal>

      <Modal
        onCancel={closeCreateBillModal}
        className="modal-charge-confirm"
        visible={
          createBillModalData.visible &&
          !createBillModalData.billIdNullPayments.length &&
          !createBillModalData.isMixingInvoices &&
          !createBillModalData.isAllNullInvoices
        }
        width={500}
        title="Создание счета на постоплату"
        footer={[
          <Button key="2" onClick={closeCreateBillModal} style={{ width: "107px" }} type="primary">
            Ок
          </Button>,
        ]}
      >
        <div
          style={{
            fontSize: "14px",
            display: "flex",
            marginLeft: "15px",
          }}
        >
          <div>{warningIcon}</div>
          <span style={{ paddingLeft: "10px", paddingTop: "2px" }}>
            Начисления, доступные для создания счетов, отсутствуют.
          </span>
        </div>
      </Modal>

      <Modal
        onCancel={closeCreateBillModal}
        className="modal-charge-confirm"
        visible={
          createBillModalData.visible &&
          createBillModalData.billIdNullPayments.length &&
          !createBillModalData.isMixingInvoices &&
          !createBillModalData.isAllNullInvoices
        }
        width={500}
        title="Создание счета на постоплату"
        footer={[
          <Button
            key="1"
            onClick={closeCreateBillModal}
            style={{
              width: "107px",
            }}
          >
            Отмена
          </Button>,
          <Button
            key="2"
            onClick={submitCreateBillModal}
            style={{ width: "127px" }}
            type="primary"
            loading={isLoading}
          >
            Продолжить
          </Button>,
        ]}
      >
        <div style={{ textAlign: "center", fontSize: "14px" }}>
          {createBillModalData.isDeletedAccountedInvoices && (
            <div style={{ display: "flex", marginLeft: "20px", marginBottom: "15px" }}>
              <div>{warningIcon}</div>
              <span style={{ paddingLeft: "15px", paddingTop: "2px" }}>
                Начисления, учтенные в счетах, были удалены из списка.
              </span>
            </div>
          )}
          <div>
            <label style={{ paddingRight: "15px" }}>Дата счета</label>
            <DatePicker
              allowClear={false}
              format={displayFormat}
              value={createBillModalData.onDate}
              onChange={(date) => {
                handleChangeCreateBillModalData({
                  onDate: date,
                });
              }}
            />
          </div>
        </div>
      </Modal>

      {renderFooter()}

      <HistoryAndPropertyModal
        objectTypeSysname={object.isAdjustment === 0 ? "service_charge" : "adjustment"}
        objectId={object?.chargeId}
        handleHideModal={handleHideModal}
        visibleModal={object?.chargeId}
        dateObjectFromForCalendar={object?.chargePeriodFrom}
        dateObjectToForCalendar={object?.chargePeriodTo}
        hasAccessToEdit={hasAccess(MENU_ACCOUNT_CHARGES_WRITE)}
      />
    </>
  );
};

export default AccountCharges;
